/**
 * Batch 18-G14 / 18-G16 — Learning Requirements display-state helper.
 *
 * Pure function that maps a `TechnicianRequirementStatus` row plus a
 * `currentDate` (and, in G16, an optional evidence summary) to a small
 * display state used by `LearningRequirementsTab`. Implements the G13
 * plan's Option B ("requirements-specific display adapter using G4
 * vocabulary") and the G15 plan's Option 1 ("extend the existing
 * helper to accept an optional evidence summary").
 *
 * Boundaries (mirroring G13 §8 and G15 §8):
 *
 *   - No Supabase / React Query primitives. Helper is synchronous
 *     and side-effect-free.
 *   - No import of the G4 ranked envelope — the requirements tab
 *     is NOT a ranking surface. The helper borrows only the G4
 *     reason-code vocabulary plus the existing technician-evidence
 *     summary status union (F32 / F36 lane).
 *   - No import of the G4 ticket-side helpers.
 *   - No browser launch / modal mount.
 *   - Forbidden vocabulary in helper output: `Certified`,
 *     `Confidence`, `Previously completed` — never rendered on
 *     this surface.
 *   - The evidence-badge labels DO appear in helper output when an
 *     `evidenceSummary` is passed in: `Verification pending`,
 *     `Confirmed by Manager`, `Verification revoked`, `Manual
 *     completion submitted`. These are evidence states, not
 *     recommendation confidence claims.
 *
 * Policy badge precedence (fixed order in `policyBadges`):
 *
 *   1. `Required by shop`              (variant `default`)
 *   2. `Manager verification required` (variant `outline`)
 *   3. `Overdue`                       (variant `destructive`)
 *   4. `Due soon`                      (variant `secondary`)
 *
 * Overdue always wins over Due soon (never both).
 *
 * Evidence badge family (G16, separate `evidenceBadges` array):
 *
 *   - `verification_pending` → `Verification pending` (secondary)
 *   - `verified`             → `Confirmed by Manager` (default; OD-35 — was `Verified by manager`)
 *   - `revoked`              → `Verification revoked` (destructive)
 *   - `manual_submitted`     → `Manual completion submitted` (outline)
 *   - `no_evidence`          → no badge
 *
 * `visibility.suppressCompleteDisplay` is flipped to `true` only when
 * the evidence summary status is `'revoked'`. The tab consumer then
 * hides the primary `Completed` badge so revoked rows never appear
 * trusted. All other statuses leave the flag `false` (G14 baseline).
 *
 * Backward compatibility: when `evidenceSummary` is omitted, the
 * helper returns G14 behaviour exactly — `evidenceBadges: []` and
 * `suppressCompleteDisplay: false`.
 *
 * Waived rows never receive the `Required by shop` badge, the
 * `Overdue` / `Due soon` badges, or a reason fragment. They also
 * never receive an evidence badge — the waived state is the
 * authoritative signal for those rows.
 */

import type { TechnicianRequirementStatus } from '@/types/training';
import type { TechnicianTrainingEvidenceSummaryStatus } from '@/utils/training/technicianEvidenceSummary';

export interface LearningRequirementPolicyBadge {
  label: string;
  testId: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
}

export interface LearningRequirementEvidenceBadge {
  label: string;
  testId: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
}

export interface LearningRequirementEvidenceSummary {
  status: TechnicianTrainingEvidenceSummaryStatus;
}

export interface LearningRequirementVisibility {
  /**
   * Flipped to `true` only when `evidenceSummary?.status === 'revoked'`
   * (G16). The tab consumer hides the primary `Completed` badge in
   * that case so revoked rows never appear trusted. G14 baseline:
   * always `false`.
   */
  suppressCompleteDisplay: boolean;
}

export type LearningRequirementPrimaryStatusLabel =
  | 'Assigned'
  | 'In Progress'
  | 'Completed'
  | 'Overdue'
  | 'Waived';

export interface LearningRequirementDisplayState {
  primaryStatusLabel: LearningRequirementPrimaryStatusLabel;
  policyBadges: LearningRequirementPolicyBadge[];
  /** Batch 18-G16 — separate from `policyBadges`; never mixed. */
  evidenceBadges: LearningRequirementEvidenceBadge[];
  reasonFragment: string | null;
  visibility: LearningRequirementVisibility;
}

const PRIMARY_STATUS_LABEL: Record<
  Exclude<TechnicianRequirementStatus['status'], 'overdue'>,
  LearningRequirementPrimaryStatusLabel
> = {
  assigned: 'Assigned',
  in_progress: 'In Progress',
  completed: 'Completed',
  waived: 'Waived',
};

/**
 * 7-day window for the `Due soon` policy badge. Mirrors the G4
 * ticket-side ranker's window so the two surfaces feel consistent.
 */
const DUE_SOON_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function parseDueDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const t = Date.parse(raw);
  if (Number.isNaN(t)) return null;
  return new Date(t);
}

/**
 * Batch 18-G16 — pure mapping from the existing evidence-summary
 * derivation (`deriveTechnicianTrainingEvidenceSummaryStatus`) to a
 * single evidence badge. Returns `null` for `no_evidence` and for
 * waived rows (handled by the caller — see `buildLearningRequirementDisplayState`).
 *
 * Label / variant / testId are pinned by the G16 source-pin.
 */
function buildEvidenceBadge(
  status: TechnicianTrainingEvidenceSummaryStatus,
): LearningRequirementEvidenceBadge | null {
  switch (status) {
    case 'verification_pending':
      return {
        label: 'Verification pending',
        testId: 'requirement-evidence-badge-verification-pending',
        variant: 'secondary',
      };
    case 'verified':
      return {
        // OD-35 (2026-10-15): the summary does not carry the manager's name, and "Verified" never appears without a
        // named verifier — a manager's sign-off is a confirmation.
        label: 'Confirmed by Manager',
        testId: 'requirement-evidence-badge-verified',
        variant: 'default',
      };
    case 'revoked':
      return {
        label: 'Verification revoked',
        testId: 'requirement-evidence-badge-revoked',
        variant: 'destructive',
      };
    case 'manual_submitted':
      return {
        label: 'Manual completion submitted',
        testId: 'requirement-evidence-badge-manual-submitted',
        variant: 'outline',
      };
    case 'no_evidence':
    default:
      return null;
  }
}

export function buildLearningRequirementDisplayState(
  row: TechnicianRequirementStatus,
  currentDate: Date,
  evidenceSummary?: LearningRequirementEvidenceSummary,
): LearningRequirementDisplayState {
  const requirement = row.requirement;
  const status = row.status;
  const isActive = requirement?.is_active !== false;
  const isWaived = status === 'waived';
  const isCompleted = status === 'completed';
  const dueDate = parseDueDate(requirement?.required_by_date);

  // Overdue derivation — mirrors the existing tab rule:
  //   `dueDate < currentDate && status !== 'completed' && status !== 'waived'`.
  // A stored `status === 'overdue'` is also honoured.
  const derivedOverdue =
    dueDate !== null &&
    dueDate.getTime() < currentDate.getTime() &&
    !isCompleted &&
    !isWaived;
  const isOverdue = status === 'overdue' || derivedOverdue;

  // Due-soon derivation — only when not already overdue and not in a
  // terminal state.
  const dueSoon =
    !isOverdue &&
    dueDate !== null &&
    !isCompleted &&
    !isWaived &&
    dueDate.getTime() >= currentDate.getTime() &&
    dueDate.getTime() - currentDate.getTime() <= DUE_SOON_WINDOW_MS;

  // Primary status label — `isOverdue` already folds in stored
  // `'overdue'` plus the derived-overdue rule, so the remaining
  // branch sees only the non-overdue statuses.
  const primaryStatusLabel: LearningRequirementPrimaryStatusLabel = isOverdue
    ? 'Overdue'
    : PRIMARY_STATUS_LABEL[
        status as Exclude<TechnicianRequirementStatus['status'], 'overdue'>
      ];

  const policyBadges: LearningRequirementPolicyBadge[] = [];

  // 1. Required by shop — every active, non-waived requirement.
  // Waived rows intentionally suppress this badge so the UX does not
  // ask the technician to act on a waived requirement.
  const requiresAction = isActive && !isWaived;
  if (requiresAction) {
    policyBadges.push({
      label: 'Required by shop',
      testId: 'requirement-badge-required-by-shop',
      variant: 'default',
    });
  }

  // 2. Manager verification required — driven by the F46 flag.
  // Never emitted for waived rows (no required-action context).
  if (
    requirement?.requires_manager_verification === true &&
    !isWaived &&
    isActive
  ) {
    policyBadges.push({
      label: 'Manager verification required',
      testId: 'requirement-badge-manager-verification-required',
      variant: 'outline',
    });
  }

  // 3. Overdue — never for waived/completed rows.
  if (isOverdue && !isWaived && !isCompleted) {
    policyBadges.push({
      label: 'Overdue',
      testId: 'requirement-badge-overdue',
      variant: 'destructive',
    });
  }

  // 4. Due soon — never for waived/completed rows. Overdue wins.
  if (dueSoon) {
    policyBadges.push({
      label: 'Due soon',
      testId: 'requirement-badge-due-soon',
      variant: 'secondary',
    });
  }

  // Reason fragment — only when the row is still in a required-action
  // state.
  let reasonFragment: string | null = null;
  if (requiresAction) {
    if (isOverdue && !isCompleted) {
      reasonFragment = 'Required by shop policy. Overdue.';
    } else if (dueSoon) {
      reasonFragment = 'Required by shop policy. Due soon.';
    } else if (
      requirement?.requires_manager_verification === true &&
      !isCompleted
    ) {
      reasonFragment = 'Required by shop policy. Manager verification required.';
    } else if (!isCompleted) {
      reasonFragment = 'Required by shop policy.';
    }
    // For `Completed` rows, leave the reason line absent —
    // the existing `Completed` status badge already speaks the
    // outcome and we do NOT render `Previously completed` in G14/G16
    // (deferred — see G15 §6 / §13).
  }

  // Batch 18-G16 — evidence badge family. Always present (possibly
  // empty). Waived rows never receive an evidence badge — the waived
  // state takes precedence and the tab UX already explains why the
  // row is excluded from required-action treatment.
  const evidenceBadges: LearningRequirementEvidenceBadge[] = [];
  if (evidenceSummary && !isWaived) {
    const badge = buildEvidenceBadge(evidenceSummary.status);
    if (badge) {
      evidenceBadges.push(badge);
    }
  }

  // Visibility — only `revoked` evidence flips
  // `suppressCompleteDisplay`. Waived rows never trigger the flag
  // (waived already suppresses Completed via the existing UX).
  const suppressCompleteDisplay =
    evidenceSummary?.status === 'revoked' && !isWaived;

  return {
    primaryStatusLabel,
    policyBadges,
    evidenceBadges,
    reasonFragment,
    visibility: { suppressCompleteDisplay },
  };
}
